import PageHeading from '../components/PageHeading';
import React, { useState } from 'react';
import { Shield, ChevronDown, Mail, Globe, MousePointer2, LifeBuoy } from 'lucide-react';

const chapters = [
  {
    number: '01',
    tag: '注册篇',
    tagColor: 'bg-accent-soft text-accent border-accent-line',
    numberColor: 'text-blue-200',
    icon: <Mail size={22} className="mx-auto text-brand" aria-hidden="true" />,
    iconBg: 'bg-accent-soft',
    title: '邮箱是决定你会不会封的第一步',
    subtitle: '注册用的邮箱，是很多人忽视的封号隐患',
    desc: '很多人以为封号是用的时候出了问题，但其实账号从注册那一刻起就已经埋下了隐患。注册邮箱的类型是核心风险因素之一——不同邮箱的封号概率差距很大。\n\n⚠️ 说在前面：选对邮箱只能降低风险，不是万能保险。账号最终稳不稳，还跟你的使用环境和使用行为直接相关（见第 02、03 章）。',
    borderColor: 'border-accent-line',
    dotColor: 'bg-accent',
    extraBlock: {
      title: '📊 注册邮箱封号风险排名',
      subtitle: '风险从高到低，供参考',
      rows: [
        { rank: '🔴 最高危', name: 'Outlook 邮箱', detail: '封号概率极高，OpenAI 对 Outlook 域名账号的清查力度最重。根据大量用户反馈，Outlook 注册的账号往往撑不过第二个月，甚至更短。强烈建议：不要用 Outlook 注册 ChatGPT，不管是自己注册还是购买，看到是 Outlook 邮箱的账号都要绕行。' },
        { rank: '🟠 次高危', name: 'Gmail 邮箱', detail: '很多人默认 Gmail 最安全，但事实并非如此。Gmail 账号短期内通常能正常使用，但长期来看被封的概率同样很高，尤其是长期使用或续费后，封号风险会明显上升。Gmail 并不是一个值得托付的长期选择。' },
        { rank: '🟡 中等风险', name: 'QQ 邮箱', detail: '相比 Outlook 和 Gmail，QQ 邮箱的封号记录少一些，偶发性封号为主。但 QQ 邮箱并非完全安全，遇上 OpenAI 批量核查时同样会被波及，不能掉以轻心。' },
        { rank: '🟢 相对安全', name: '163 邮箱 / Mail 邮箱 / 自建邮箱', detail: '目前来看，这几类邮箱被批量封禁的记录最少，安全性相差不大，选哪个都可以。163、mail.com 属于小众邮箱，不在 OpenAI 重点审查名单；自建域名邮箱同样如此。\n\n💡 我们出售的 ChatGPT 成品号，全部由真人手工注册，使用的正是这类安全性更高的邮箱，来源和注册环境均可查验——这是我们和市面上批量脚本号最本质的区别。' },
      ]
    },
    items: [
      {
        risk: true,
        title: '用临时邮箱或一次性邮箱注册',
        desc: 'OpenAI 会定期核查账号邮箱的真实性。10 分钟邮箱、Guerrilla Mail 等临时邮箱注册的账号，在批量清理时会直接被封，没有任何申诉空间。'
      },
      {
        risk: true,
        title: '通过第三方批量注册平台购买账号',
        desc: '网上卖的批量账号通常由脚本自动生成，注册特征雷同，OpenAI 很容易识别并整批封禁。低价批量账号几乎都属于这一类，买了也是在花钱买隐患。\n\n✅ 我们的账号和这类完全不同——全部由真人在安全的网络环境下手动注册，使用的是 Mail 类安全邮箱，每个账号独立注册、独立环境，不存在批量特征，来源清晰可查。'
      },
      {
        risk: false,
        title: '用 163 / Mail / 自建域名邮箱注册',
        desc: '这几类邮箱目前封号记录最少，是注册 ChatGPT 相对稳妥的选择。如果你要自己注册，优先考虑这些邮箱，同时确保注册时使用干净固定的海外节点。'
      },
      {
        risk: false,
        title: '通过正规渠道购买的成品号',
        desc: '正规成品号是在干净环境下用真实邮箱注册的，来源可查。相比自己在国内复杂环境下注册，账号基础反而更稳。我们提供的账号均属于此类。'
      },
    ]
  },
  {
    number: '02',
    tag: '环境篇',
    tagColor: 'bg-accent-soft text-accent border-accent-line',
    numberColor: 'text-purple-200',
    icon: <Globe size={22} className="mx-auto text-brand" aria-hidden="true" />,
    iconBg: 'bg-accent-soft',
    title: '你在什么环境下使用？',
    subtitle: '网络环境是 OpenAI 风控系统最核心的判断依据',
    desc: 'OpenAI 的风控系统会持续监控你的登录 IP 来源、登录频次和地理位置变化。即使账号本身没有任何问题，在一个"不干净"的环境下使用，同样会触发封禁。',
    borderColor: 'border-accent-line',
    dotColor: 'bg-accent',
    items: [
      {
        risk: true,
        title: '使用来路不明的免费代理或公共 VPN',
        desc: '免费 VPN 的 IP 池通常已被大量用户用于违规行为，这些 IP 已被 OpenAI 标记为高风险。用这类节点登录，系统会对你的账号额外加严审查。'
      },
      {
        risk: true,
        title: '频繁切换 IP 节点',
        desc: '今天用美国节点、明天用日本节点、后天用英国节点——这种行为特征和账号盗用高度相似，系统会认为你的账号正在被多人使用或被盗用。'
      },
      {
        risk: true,
        title: '使用共享 IP（多人同用一个节点）',
        desc: '如果你和别人共用同一个代理 IP，当其中某个人的行为触发风控，这个 IP 上的所有账号都会被连带审查，极易造成误杀。'
      },
      {
        risk: false,
        title: '固定一个干净的独立节点',
        desc: '选择稳定、独立、未被污染的美国或日本节点，长期固定使用同一个。OpenAI 的系统会逐渐建立对你使用模式的信任，大幅降低风控触发概率。'
      },
      {
        risk: false,
        title: '避免在公共 WiFi 或公司网络下登录',
        desc: '公共 WiFi 和公司网络往往是共享 IP，其他人的行为会影响到你。建议始终通过自己的固定代理节点访问 ChatGPT。'
      },
    ]
  },
  {
    number: '03',
    tag: '行为篇',
    tagColor: 'bg-brand-soft text-brand border-brand-line',
    numberColor: 'text-orange-200',
    icon: <MousePointer2 size={22} className="mx-auto text-brand" aria-hidden="true" />,
    iconBg: 'bg-brand-soft',
    title: '你在怎么使用账号？',
    subtitle: '使用行为是最容易被忽视、也最容易主动触雷的一环',
    desc: '账号干净、环境干净，不代表就万事大吉。很多人是因为日常使用习惯出了问题导致封号，而这些问题完全是可以主动避免的。',
    borderColor: 'border-brand-line',
    dotColor: 'bg-brand',
    items: [
      {
        risk: true,
        title: '把账号借给别人使用',
        desc: '多人同时使用同一个账号，登录地点瞬间变成多个国家，系统立刻判定为账号被盗用，封号几乎是必然的。一个账号只能由一个人固定使用。'
      },
      {
        risk: true,
        title: '使用第三方镜像站或破解工具',
        desc: '国内的 ChatGPT 镜像站通常需要你输入账号密码，一旦输入，账号信息就已经泄露。同时这类站点的访问行为本身会被 OpenAI 检测到。'
      },
      {
        risk: true,
        title: '发送违规内容',
        desc: 'ChatGPT 的内容审核系统是实时运行的。发送涉及暴力、色情、仇恨言论、武器制造等违禁内容，系统会自动触发封号，没有申诉空间。'
      },
      {
        risk: true,
        title: '用脚本或自动化工具批量刷请求',
        desc: '短时间内发送大量请求、使用机器人脚本自动化调用，OpenAI 的反机器人系统会直接识别并封禁。大量 API 需求请使用官方 API 接口，不要用 ChatGPT 网页端。'
      },
      {
        risk: false,
        title: '像正常人一样使用',
        desc: '正常的聊天节奏、固定的时间段、合理的请求频次，这就是最好的防封策略。ChatGPT 是设计给真人用的，模拟真人行为是最安全的使用方式。'
      },
    ]
  },
  {
    number: '04',
    tag: '补救篇',
    tagColor: 'bg-red-50 text-red-600 border-red-200',
    numberColor: 'text-red-200',
    icon: <LifeBuoy size={22} className="mx-auto text-brand" aria-hidden="true" />,
    iconBg: 'bg-red-50',
    title: '已经被封了，怎么办？',
    subtitle: '先冷静，了解封号类型，再决定怎么应对',
    desc: '收到封号邮件或账号无法登录，不要慌。先搞清楚是哪种情况，再决定是申诉还是换号。不同类型的封号处理方式差别很大。',
    borderColor: 'border-red-100',
    dotColor: 'bg-red-400',
    items: [
      {
        risk: null,
        title: '确认封号原因',
        desc: 'OpenAI 的封号邮件通常会说明原因。常见原因有：违规内容、异常登录、账号被盗用、违反使用条款。搞清楚原因再决定下一步。'
      },
      {
        risk: null,
        title: '尝试申诉：appeals@openai.com',
        desc: '如果你认为封号有误（比如异常登录是你自己操作导致的），可以发邮件到 appeals@openai.com 申诉。说明账号情况和你的使用场景，OpenAI 通常 3–7 个工作日回复。'
      },
      {
        risk: true,
        title: '申诉成功率并不高',
        desc: '因为违规内容被封的账号申诉基本无效。因为异常登录或误判被封的申诉成功率稍高一些，但整体不到 30%。不要把希望全押在申诉上。'
      },
      {
        risk: false,
        title: '换号重来，这次用对方法',
        desc: '被封的账号数据无法找回，大多数情况下换号是最务实的选择。重新从正规渠道购买账号，按照本文的建议使用，第二次不会再被封。'
      },
    ]
  }
];

const faqData = [
  {
    question: '你们既卖代充也卖成品号，这两种会导致封号吗？',
    answer: '都不会。\n\n代充是在你自己的 ChatGPT 账号上充值升级为 Plus，账号一直在你手里，充值过程无需提供密码，OpenAI 看到的只是一笔正常的订阅付款，不存在任何触发封号的行为。\n\n成品号是我们用真实邮箱、在干净网络环境下手动注册好的账号，直接交付给你使用。账号来源正规，注册环境干净，本身没有任何违规记录。\n\n封号的根本原因是使用行为和环境，而不是账号的来源。两种产品只要按本指南的建议正确使用，都可以长期稳定运行。'
  },
  {
    question: '成品号和代充，我应该选哪个？',
    answer: '看你的需求。\n\n如果你已经有自己的 ChatGPT 账号，只是需要升级到 Plus，选代充——账号还是你的，最省事。\n\n如果你没有账号，或者不想自己折腾注册，选成品号——买到即用，我们的账号注册环境干净，邮箱安全可控，省去自己注册可能踩的坑。'
  },
  {
    question: '我只是普通用户，每天聊几十条消息，会封号吗？',
    answer: '正常使用几乎不会封号。ChatGPT 本来就是为日常使用设计的，只要网络环境固定、不共享账号、不发违规内容，账号可以长期稳定使用。'
  },
  {
    question: '申诉成功率高吗？被封了一定要申诉吗？',
    answer: '申诉成功率整体不高，因违规内容被封的几乎没有申诉空间；因异常登录被封的稍微好一点，但也不超过三成。\n\n大多数情况下换号重来是最务实的选择——被封账号的数据无法找回，与其花时间等申诉结果，不如从正规渠道重新购买一个，这次按指南规范使用。'
  },
];

export default function AntiBanGuide() {

  const [openFaqIndex, setOpenFaqIndex] = useState(null);



  return (
    <div className="min-h-screen bg-canvas">

      {/* Hero */}
      <section className="relative container mx-auto px-4 pt-12 pb-10 md:pt-16 md:pb-12">
        <div className="max-w-4xl mx-auto">
          <PageHeading Icon={Shield} eyebrow="GUIDE · 账号使用" title="了解风险，安心使用" description="从注册、环境、使用习惯到问题处理，了解账号使用注意事项。" />

          {/* 四章导航 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto mb-6">
            {chapters.map((ch) => (
              <a
                key={ch.number}
                href={`#chapter-${ch.number}`}
                className="border border-line rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 bg-white text-center"
              >
                <div className="text-xl mb-1">{ch.icon}</div>
                <div className="text-xs font-bold text-muted mb-0.5">第 {ch.number} 章</div>
                <div className="text-sm font-bold text-ink">{ch.tag.replace('篇', '')}</div>
              </a>
            ))}
          </div>

          {/* 购买渠道说明 */}
          <p className="text-xs text-muted text-center leading-relaxed">
            <span className="mr-1">✅</span>
            从正规渠道购买账号，购买行为本身不会导致封号。封号风险来自使用环境和行为，与账号来源无关。
          </p>
        </div>
      </section>

      {/* 四个篇章 */}
      {chapters.map((ch, ci) => (
        <section
          key={ch.number}
          id={`chapter-${ch.number}`}
          style={{ scrollMarginTop: 88 }}
          className={`py-16 ${ci % 2 === 1 ? 'bg-canvas' : ''}`}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">

              {/* 篇章 header */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-xs font-bold tracking-widest px-3 py-1.5 rounded-full border ${ch.tagColor}`}>
                    第 {ch.number} 章 · {ch.tag}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-ink mb-2">{ch.title}</h2>
                <p className="text-sm font-semibold text-muted mb-4">{ch.subtitle}</p>
                {ch.desc.split('\n\n').map((para, pi) => (
                  <p key={pi} className="text-sm text-muted leading-relaxed max-w-2xl mb-2">{para}</p>
                ))}
              </div>

              {/* 产品推荐卡片（仅部分章节） */}
              {ch.productCard && (
                <div className="mb-8 border-2 border-gray-900 rounded-2xl p-7 relative overflow-hidden bg-white hover:shadow-panel transition-all duration-300">
                  <div className="absolute top-0 right-0 bg-brand text-white text-xs font-bold px-4 py-2 rounded-bl-2xl rounded-tr-3xl tracking-wide">
                    ⚡ 推荐工具
                  </div>
                  <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border mb-4 ${ch.productCard.badgeColor}`}>
                    <span>{ch.productCard.badge}</span>
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-1">{ch.productCard.title}</h3>
                  <p className="text-sm font-semibold text-muted mb-3">{ch.productCard.subtitle}</p>
                  <p className="text-sm text-muted leading-relaxed mb-5">{ch.productCard.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {ch.productCard.highlights.map((h, hi) => (
                      <li key={hi} className="flex items-center gap-2 text-sm text-ink">
                        <span className="w-1.5 h-1.5 bg-brand rounded-full flex-shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <a
                      href={ch.productCard.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white text-sm font-bold rounded-xl hover:bg-brand transition-all duration-300 shadow-lg"
                    >
                      {ch.productCard.cta} →
                    </a>
                    {ch.productCard.discount && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand bg-brand-soft border border-brand-line px-3 py-1.5 rounded-full">
                        🎁 {ch.productCard.discount}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* 邮箱风险排名（仅注册篇） */}
              {ch.extraBlock && (
                <div className="mb-8 border-2 border-accent-line rounded-2xl overflow-hidden">
                  <div className="bg-accent-soft px-6 py-4 border-b border-accent-line">
                    <h3 className="text-base font-bold text-accent">{ch.extraBlock.title}</h3>
                    <p className="text-xs text-accent mt-0.5">{ch.extraBlock.subtitle}</p>
                  </div>
                  <div className="divide-y divide-blue-50 bg-white">
                    {ch.extraBlock.rows.map((row, ri) => (
                      <div key={ri} className="px-6 py-4 flex items-start gap-4">
                        <span className="text-sm font-bold whitespace-nowrap pt-0.5">{row.rank}</span>
                        <div>
                          <span className="text-sm font-bold text-ink">{row.name}</span>
                          {row.detail.split('\n\n').map((para, pi) => (
                            <p key={pi} className="text-xs text-muted leading-relaxed mt-1">{para}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 条目 */}
              <div className="space-y-4">
                {ch.items.map((item, ii) => (
                  <div
                    key={ii}
                    className={`bg-white border-2 ${ch.borderColor} rounded-2xl p-6 hover:shadow-panel hover:-translate-y-0.5 transition-all duration-300 flex items-start gap-4`}
                  >
                    {/* 风险标记 */}
                    <div className="flex-shrink-0 mt-0.5">
                      {item.risk === true && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-500 bg-red-50 border border-red-100 px-2 py-1 rounded-full whitespace-nowrap">
                          ❌ 高危
                        </span>
                      )}
                      {item.risk === false && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 border border-green-100 px-2 py-1 rounded-full whitespace-nowrap">
                          ✅ 推荐
                        </span>
                      )}
                      {item.risk === null && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-muted bg-canvas border border-line px-2 py-1 rounded-full whitespace-nowrap">
                          📌 注意
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-ink mb-1">{item.title}</h3>
                      {item.desc.split('\n\n').map((para, pi) => (
                        <p key={pi} className="text-sm text-muted leading-relaxed mt-1">{para}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* 快速总结 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-bold text-brand tracking-[0.3em] text-center mb-3">QUICK SUMMARY</p>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-ink">防封三条核心原则</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { emoji: '📧', title: '注册邮箱安全可控', desc: '杜绝 Outlook、Gmail，QQ 邮箱也要谨慎，优先选 163、Mail 或自建邮箱。' },
                { emoji: '🌐', title: '账号环境和 IP 独享', desc: '一个账号用一个固定节点，不共享 IP，不频繁切换地区。' },
                { emoji: '🖱️', title: '正常人的用法', desc: '一人一号，不共享，不发违规内容，不用脚本自动化。' },
              ].map((card, i) => (
                <div key={i} className="border border-line rounded-2xl p-6 hover:shadow-panel  transition-all duration-300 bg-white text-center">
                  <div className="w-12 h-12 bg-brand-soft rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-2xl">{card.emoji}</span>
                  </div>
                  <h3 className="text-base font-bold text-ink mb-2">{card.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-canvas py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 text-ink">常见问题</h2>
            <p className="text-sm text-muted text-center mb-10">整理了最常被问到的封号相关问题</p>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-line rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-panel"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="text-base font-semibold text-ink pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 space-y-2">
                      {faq.answer.split('\n\n').map((para, pi) => (
                        <p key={pi} className="text-sm text-muted leading-relaxed">{para}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 pb-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="border-2 border-black rounded-2xl p-10 hover:shadow-panel transition-all duration-300">
            <div className="w-12 h-12 bg-brand rounded-2xl flex items-center justify-center mb-5 mx-auto">
              <span className="text-xl">🛒</span>
            </div>
            <h2 className="text-2xl font-bold text-ink mb-3">准备好安全使用了吗？</h2>
            <p className="text-sm text-muted mb-6 leading-relaxed">
              从正规渠道购买账号，是防封的第一步。我们提供来源可查的 Plus 代充和成品号，30 天质保兜底。
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand text-white text-base font-bold rounded-2xl hover:bg-brand transform  transition-all duration-300 shadow-panel"
            >
              查看充值套餐
              <span className="text-lg">→</span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
